import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CheckCircle2 } from "lucide-react";
import PageWrapper from "@/components/layout/PageWrapper";
import Breadcrumb from "@/components/Breadcrumb";
import Icon from "@/components/Icon";
import { useLanguage } from "@/i18n/LanguageContext";
import { SCHOOL_SERVICES, IMAGES } from "@/data/content";

const Services = () => {
  const { t } = useLanguage();

  return (
    <PageWrapper
      title="Services — ADHD School Bhadrak | OT, PT, Speech, Sensory & Special Education"
      description="Therapy services and special education at ADHD School Bhadrak — Occupational Therapy, Physiotherapy, Speech Therapy, Sensory Integration & individualised special education."
    >
      <section className="adhd-container pt-10 lg:pt-14">
        <Breadcrumb pageKey="nav.services" />
        <p className="adhd-eyebrow mt-6">
          <BookOpen size={13} /> Therapy & Education
        </p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-[color:var(--adhd-text)] max-w-3xl">
          {t("services.title")}
        </h1>
        <p className="mt-4 text-base sm:text-lg text-[color:var(--adhd-muted)] max-w-2xl">
          {t("services.subtitle")}
        </p>
      </section>

      <section className="adhd-container mt-12 grid lg:grid-cols-12 gap-5">
        {SCHOOL_SERVICES.map((s, i) => (
          <article
            key={s.id}
            data-testid={`service-detail-${s.id}`}
            className={`adhd-card p-7 sm:p-8 flex flex-col gap-5 ${
              i === 0 ? "lg:col-span-7" : i === 1 ? "lg:col-span-5" : "lg:col-span-4"
            }`}
          >
            <div className="flex items-center justify-between">
              <span
                className="grid place-items-center h-14 w-14 rounded-2xl"
                style={{
                  background: "rgba(58,124,165,0.10)",
                  color: "var(--adhd-primary)",
                }}
              >
                <Icon name={s.icon} size={24} />
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-[color:var(--adhd-muted)]">
                0{i + 1}
              </span>
            </div>
            <div>
              <h3 className="font-serif text-2xl sm:text-3xl text-[color:var(--adhd-text)] leading-snug">
                {s.title}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--adhd-muted)]">
                {s.desc}
              </p>
            </div>
            <ul className="grid grid-cols-1 gap-1.5 text-sm text-[color:var(--adhd-text)]/85">
              <li className="flex items-center gap-2"><CheckCircle2 size={14} style={{ color: "var(--adhd-success)" }} /> One-on-one sessions</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} style={{ color: "var(--adhd-success)" }} /> Progress tracking with parents</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} style={{ color: "var(--adhd-success)" }} /> Certified, caring therapists</li>
            </ul>
          </article>
        ))}
      </section>

      <section
        className="adhd-section mt-16"
        style={{ background: "var(--adhd-bg-soft)" }}
      >
        <div className="adhd-container grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="aspect-[4/3] rounded-[2rem] overflow-hidden">
              <img src="/services-howitworks.jpeg" alt="Therapy session" loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="adhd-eyebrow">How it works</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[color:var(--adhd-text)]">
              A child-first approach, every single session.
            </h2>
            <ol className="mt-6 flex flex-col gap-5">
              {[
                { n: "01", t: "Initial Assessment", d: "We meet you and your child, observe gently, and listen to your concerns." },
                { n: "02", t: "Individualised Educational Plan", d: "Special children are different from each other, so their educational plans should be designed according to their individual needs, strengths, and learning goals." },
                { n: "03", t: "Therapy + Learning", d: "Structured sessions with consistent therapists in a warm, low-stimulation space." },
                { n: "04", t: "Parent Partnership", d: "Regular updates, home-practice tips and quarterly progress reviews." },
              ].map((step) => (
                <li key={step.n} className="flex gap-4">
                  <span className="font-serif text-xl text-[color:var(--adhd-primary)] min-w-[2.5rem]">
                    {step.n}
                  </span>
                  <div>
                    <p className="font-semibold text-[color:var(--adhd-text)]">{step.t}</p>
                    <p className="text-sm text-[color:var(--adhd-muted)] mt-1 leading-relaxed">{step.d}</p>
                  </div>
                </li>
              ))}
            </ol>
            <Link to="/contact" className="mt-8 adhd-btn-primary inline-flex" data-testid="services-cta">
              Book an Assessment <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </PageWrapper>
  );
};

export default Services;
